import { Changeset } from 'ember-changeset';
import lookupValidator from 'ember-changeset-validations';
import { validatePresence } from 'ember-changeset-validations/validators';
import IntlService from 'ember-intl/services/intl';
import { BufferedChangeset } from 'validated-changeset';
import { TOrderSpikeHorizonUnit } from 'volta/models/types/planning-settings';
import { round } from 'volta/utils/math-utils';

import { action } from '@ember/object';
import { inject as service } from '@ember/service';
import { isPresent } from '@ember/utils';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';

const Validations = {
  orderSpikeHorizon: [],
  orderSpikeThreshold: [],
  orderSpikeHorizonUnit: [validatePresence(true)],
  orderSpikeLtHorizon: [],
  orderSpikeLtThreshold: [],
  orderSpikeLtHorizonUnit: [validatePresence(true)]
};

export interface IOrderSpikeSubmit {
  orderSpikeHorizon?: number;
  orderSpikeThreshold?: number;
  orderSpikeHorizonUnit?: TOrderSpikeHorizonUnit;
  orderSpikeLtHorizon?: number;
  orderSpikeLtThreshold?: number;
  orderSpikeLtHorizonUnit?: TOrderSpikeHorizonUnit;
}
interface IArgs {
  dlt?: number;
  orderCycle?: number;
  orderSpikeHorizon?: number;
  orderSpikeThreshold?: number;
  orderSpikeHorizonUnit?: TOrderSpikeHorizonUnit;
  orderSpikeLtHorizon?: number;
  orderSpikeLtThreshold?: number;
  orderSpikeLtHorizonUnit?: TOrderSpikeHorizonUnit;
  onSubmit?: (data: IOrderSpikeSubmit) => void;
}

export default class OrderSpikeFormEditor extends Component<IArgs> {
  // Services
  // ~~~~~

  @service intl!: IntlService;

  // Properties
  // ~~~~~

  @tracked model: IOrderSpikeSubmit = {};
  @tracked changeset!: BufferedChangeset;

  // Callbacks
  // ~~~~~

  // Computed Properties
  // ~~~~~

  get unitIsDays() {
    return this.changeset.get('orderSpikeHorizonUnit') === 'DAYS';
  }

  get ltUnitIsDays() {
    return this.changeset.get('orderSpikeLtHorizonUnit') === 'DAYS';
  }

  get unitOptions() {
    return [
      { key: 'DAYS', label: this.intl.t('days') },
      { key: 'PERCENT_DLT', label: this.intl.t('planningSettings.orderSpike.percentOfDLT') }
    ];
  }

  get selectedUnit() {
    const unit = this.changeset.get('orderSpikeHorizonUnit');
    return this.unitOptions.find((u) => u.key === unit);
  }

  get selectedLtUnit() {
    const unit = this.changeset.get('orderSpikeLtHorizonUnit');
    return this.unitOptions.find((u) => u.key === unit);
  }

  get maxOrderSpikeHorizon() {
    const { dlt, orderCycle } = this.args;
    return isPresent(dlt) ? (dlt as number) + (orderCycle ?? 0) : undefined;
  }

  get isHorizonOverMax() {
    const { maxOrderSpikeHorizon, unitIsDays, changeset } = this;
    if (!maxOrderSpikeHorizon) {
      return false;
    }
    let horizon = changeset.get('orderSpikeHorizon');
    if (!unitIsDays) {
      horizon = this.translateHorizonUnit(horizon, 'PERCENT_DLT', this.args.dlt);
    }
    return horizon > maxOrderSpikeHorizon;
  }

  get isLtHorizonOverMax() {
    const { maxOrderSpikeHorizon, ltUnitIsDays, changeset } = this;
    if (!maxOrderSpikeHorizon) {
      return false;
    }
    let horizon = changeset.get('orderSpikeLtHorizon');
    if (!ltUnitIsDays) {
      horizon = this.translateHorizonUnit(horizon, 'PERCENT_DLT', this.args.dlt);
    }
    return horizon > maxOrderSpikeHorizon;
  }

  // Lifecycle Hooks
  // ~~~~~

  constructor(owner: any, args: IArgs) {
    super(owner, args);
    this.initModel();
  }

  // Actions
  // ~~~~~

  @action
  handleHorizonChange(value?: number) {
    this.changeset.set('orderSpikeHorizon', this.unitIsDays ? round(value, 0) : value);
    this.onSubmit();
  }

  @action
  handleLtHorizonChange(value?: number) {
    this.changeset.set('orderSpikeLtHorizon', this.ltUnitIsDays ? round(value, 0) : value);
    this.onSubmit();
  }

  @action
  handleThresholdChange(value: number) {
    this.changeset.set('orderSpikeThreshold', value);
    this.onSubmit();
  }

  @action
  handleLtThresholdChange(value: string | number) {
    this.changeset.set('orderSpikeLtThreshold', value);
    this.onSubmit();
  }

  @action
  handleUnitChange(option: { key: TOrderSpikeHorizonUnit }) {
    if (this.changeset.get('orderSpikeHorizonUnit') === option.key) {
      return;
    }
    this.changeset.set(
      'orderSpikeHorizon',
      this.translateHorizonUnit(
        this.changeset.get('orderSpikeHorizon'),
        this.changeset.get('orderSpikeHorizonUnit'),
        this.args.dlt
      )
    );
    this.changeset.set('orderSpikeHorizonUnit', option.key);

    this.onSubmit();
  }

  @action
  handleLtUnitChange(option: { key: TOrderSpikeHorizonUnit }) {
    if (this.changeset.get('orderSpikeLtHorizonUnit') === option.key) {
      return;
    }
    this.changeset.set(
      'orderSpikeLtHorizon',
      this.translateHorizonUnit(
        this.changeset.get('orderSpikeLtHorizon'),
        this.changeset.get('orderSpikeLtHorizonUnit'),
        this.args.dlt
      )
    );
    this.changeset.set('orderSpikeLtHorizonUnit', option.key);

    this.onSubmit();
  }

  @action
  onSubmit() {
    this.changeset.execute();
    this.args.onSubmit?.(this.getDataToSubmit());
  }

  // Helper functions
  // ~~~~~

  initModel() {
    this.model = {
      orderSpikeHorizon: this.args.orderSpikeHorizon,
      orderSpikeThreshold: this.args.orderSpikeThreshold,
      orderSpikeHorizonUnit: this.args.orderSpikeHorizonUnit ?? 'DAYS',
      orderSpikeLtHorizon: this.args.orderSpikeLtHorizon,
      orderSpikeLtThreshold: this.args.orderSpikeLtThreshold,
      orderSpikeLtHorizonUnit: this.args.orderSpikeLtHorizonUnit ?? 'DAYS'
    };
    this.changeset = Changeset(this.model, lookupValidator(Validations), Validations);
  }

  translateHorizonUnit(horizon: number, unit: TOrderSpikeHorizonUnit = 'DAYS', dlt?: number) {
    return unit === 'DAYS'
      ? isPresent(horizon)
        ? dlt
          ? round(horizon / dlt)
          : 1
        : 1
      : isPresent(dlt) && isPresent(horizon)
      ? round(dlt! * horizon, 0)
      : 1;
  }

  getDataToSubmit(): IOrderSpikeSubmit {
    return { ...this.model };
  }
}
